/** Gear button + graphics settings panel (persisted by the caller via render/settings.ts). */
import {
  DEFAULT_SETTINGS,
  type GraphicsSettings,
  QUALITY_PRESETS,
  type QualityPreset,
  RENDERER_KINDS,
  type RendererKind,
  SETTING_RANGES,
  TIME_OF_DAY_MODES,
  type TimeOfDayMode,
} from '../render/settings';
import { DEFAULT_COMBAT_TEXT, combatTextSettings, mountCombatTextControls } from './combatTextSettings';
import { mountProcOverlayControl, procOverlaySettings } from './cluster/procOverlaySettings';
import { DEFAULT_COMBAT_AUDIO, combatAudioSettings, mountCombatAudioControls } from '../audio/settings'; // +impact
import { DEFAULT_CONTROLS, controlSettings, mountControlsControls } from '../input/controlSettings'; // +controls
import { el, onPress } from './dom';
import { roundButton } from './kit/roundButton';
import { mountUiScaleControls, uiScaleSettings } from './uiScale';
import { DEFAULT_GAMEPLAY, gameplaySettings, mountGameplayControls } from './gameplaySettings'; // +castux
import { mountCameraControls } from './cameraControls'; // +camera
import { cameraSettings } from '../render/cameraSettings'; // +camera

const RENDERER_LABEL: Record<RendererKind, string> = { toon: 'Toon (art style)', placeholder: 'Placeholder (debug)' };
const QUALITY_LABEL: Record<QualityPreset, string> = { low: 'Low', medium: 'Medium', high: 'High' };
const TOD_LABEL: Record<TimeOfDayMode, string> = { cycle: 'Cycle', day: 'Always day' }; // +atmos

/** Open / close the mounted settings panel (menu hotkey O). No-op when none is mounted. */
let activeToggle: ((open?: boolean) => void) | null = null;
export function toggleSettingsPanel(open?: boolean): void {
  activeToggle?.(open);
}

export function mountSettingsPanel(parent: HTMLElement, get: () => GraphicsSettings, onChange: (s: GraphicsSettings) => void): () => void {
  const root = el('div', 'gfx-settings', parent);
  const gearBtn = roundButton(root, { icon: 'settings', keybind: 'O', label: 'Settings', size: 'sm', className: 'gfx-gear', onPress: () => toggle() });
  const gear = gearBtn.root;
  gear.setAttribute('aria-expanded', 'false');
  const panel = el('div', 'panel gfx-panel hk-settings hidden', root);
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', 'Graphics settings');
  el('div', 'gfx-title', panel, 'Graphics');

  const row = (label: string): HTMLElement => {
    const r = el('label', 'gfx-row', panel);
    el('span', '', r, label);
    return r;
  };
  const emit = (patch: Partial<GraphicsSettings>): void => onChange({ ...get(), ...patch });

  const renderer = el('select', '', row('Renderer'));
  for (const k of RENDERER_KINDS) el('option', '', renderer, RENDERER_LABEL[k]).value = k;
  renderer.addEventListener('change', () => emit({ renderer: renderer.value as RendererKind }));

  const quality = el('select', '', row('Quality'));
  for (const q of QUALITY_PRESETS) el('option', '', quality, QUALITY_LABEL[q]).value = q;
  quality.addEventListener('change', () => emit({ quality: quality.value as QualityPreset }));

  // +atmos: outdoor time of day.
  const tod = el('select', '', row('Time of day'));
  for (const m of TIME_OF_DAY_MODES) el('option', '', tod, TOD_LABEL[m]).value = m;
  tod.addEventListener('change', () => emit({ timeOfDay: tod.value as TimeOfDayMode }));

  const slider = (label: string, key: 'floorBrightness' | 'fxIntensity' | 'shadowHue', fmt: (v: number) => string): (() => void) => {
    const r = row(label);
    const out = el('output', 'gfx-val', r);
    const input = el('input', '', r);
    input.type = 'range';
    const range = SETTING_RANGES[key];
    input.min = String(range.min);
    input.max = String(range.max);
    input.step = String(range.step);
    input.addEventListener('input', () => {
      out.textContent = fmt(Number(input.value));
      emit({ [key]: Number(input.value) } as Partial<GraphicsSettings>);
    });
    return () => {
      input.value = String(get()[key]);
      out.textContent = fmt(get()[key]);
    };
  };
  const pct = (v: number): string => `${Math.round(v * 100)}%`;
  const syncFloor = slider('Floor brightness', 'floorBrightness', pct);
  const syncFx = slider('Effects intensity', 'fxIntensity', pct);
  const syncHue = slider('Shadow hue', 'shadowHue', (v) => `${Math.round(v)}°`);

  const check = (label: string, key: 'outlines' | 'bloom' | 'screenShake'): (() => void) => {
    const r = row(label);
    r.classList.add('check');
    const input = el('input', '', r);
    input.type = 'checkbox';
    input.addEventListener('change', () => emit({ [key]: input.checked } as Partial<GraphicsSettings>));
    return () => {
      input.checked = get()[key];
    };
  };
  const syncOutlines = check('Outlines', 'outlines');
  const syncBloom = check('Bloom', 'bloom');
  const syncShake = check('Screen shake', 'screenShake');
  const syncGameplay = mountGameplayControls(panel); // +castux: quick cast ground spells, cast ring
  const syncCombatText = mountCombatTextControls(panel);
  const syncCombatAudio = mountCombatAudioControls(panel); // +impact
  const syncControls = mountControlsControls(panel); // +controls
  const syncCamera = mountCameraControls(panel); // +camera
  const syncProcOverlay = mountProcOverlayControl(panel);
  const syncUiScale = mountUiScaleControls(panel);

  el('div', 'gfx-hint', panel, 'Low quality: no bloom, no prop outlines, no shadows, 1x pixel ratio. Settings are saved in this browser.');
  const reset = el('button', 'btn', panel, 'Reset to defaults');
  reset.type = 'button';
  onPress(reset, () => {
    onChange({ ...DEFAULT_SETTINGS });
    combatTextSettings.set({ ...DEFAULT_COMBAT_TEXT });
    gameplaySettings.set({ ...DEFAULT_GAMEPLAY }); // +castux
    combatAudioSettings.set({ ...DEFAULT_COMBAT_AUDIO }); // +impact
    controlSettings.set({ ...DEFAULT_CONTROLS }); // +controls
    cameraSettings.set({ follow: 'never' }); // +camera (zoom is kept)
    procOverlaySettings.set(true);
    uiScaleSettings.set('auto');
    sync();
  });

  const sync = (): void => {
    const s = get();
    renderer.value = s.renderer;
    quality.value = s.quality;
    tod.value = s.timeOfDay; // +atmos
    syncFloor();
    syncFx();
    syncHue();
    syncOutlines();
    syncBloom();
    syncShake();
    syncGameplay(); // +castux
    syncCombatText();
    syncCombatAudio(); // +impact
    syncControls(); // +controls
    syncCamera(); // +camera
    syncProcOverlay();
    syncUiScale();
  };
  sync();

  const toggle = (open?: boolean): void => {
    const show = open ?? panel.classList.contains('hidden');
    panel.classList.toggle('hidden', !show);
    gear.setAttribute('aria-expanded', String(show));
    gearBtn.setActive(show);
    if (show) sync();
  };
  activeToggle = toggle;
  const onKey = (e: KeyboardEvent): void => {
    if (e.code === 'Escape' && !panel.classList.contains('hidden')) {
      toggle(false);
      e.stopPropagation();
    }
  };
  window.addEventListener('keydown', onKey, true);
  return () => {
    window.removeEventListener('keydown', onKey, true);
    if (activeToggle === toggle) activeToggle = null;
    root.remove();
  };
}
